/* eslint-disable clubmed/no-fake-component */
import type { DefaultThemeStyles, ThemeKey } from 'Src/types/theme';
import type { CSSProperties, ReactNode } from 'react';
import React, { forwardRef } from 'react';
import { classnames } from 'Utils/classnames/classnames.utils';
import { COLORS } from 'Components/utils/color/colors';
import { ICONS } from 'Components/utils/icon/icons';
import { BEFORE_APP } from 'Components/frameworks/portal/Portal.constants';
import { t } from 'Src/i18n';
import { useResponsive } from 'Components/utils/responsive/ResponsiveContext';
import type { IconName } from 'Components/frameworks/icon/Icon';
import { Icon } from 'Components/frameworks/icon/Icon';
import Portal from 'Components/frameworks/portal/Portal';
import { Text } from 'Components/frameworks/text/Text';
import { themes } from './Toast.themes';
type Props = {
  children?: ReactNode;
  className?: string;
  color?: Colors;
  // Closes the toast on its own after this many ms, the time left shown as a shrinking bar.
  // Hovering or focusing the toast pauses it.
  duration?: number;
  icon?: IconName;
  isFixed?: boolean;
  label?: string;
  onClose: () => unknown;
  portalName?: string;
  role?: 'alert' | 'status';
  // Advanced theme: a bold title over a text, both already translated; the text takes markdown.
  text?: string;
  theme?: ThemeKey<typeof themes>;
  title?: string;
  visible?: boolean;
};
export const Toast = forwardRef<HTMLDivElement, Props>(
  (
    {
      children,
      className,
      color = COLORS.BLUE_WAVE,
      duration,
      icon,
      isFixed = false,
      label,
      onClose,
      portalName = BEFORE_APP,
      role,
      text,
      theme = 'default',
      title,
      visible,
      ...rest
    }: Props,
    ref,
  ) => {
    const { isAppMounted } = useResponsive();

    if (!isAppMounted) {
      return null;
    }

    const { thCard, thClose, thCloseIconWidth, thContent } = themes[theme] as DefaultThemeStyles<
      typeof themes
    >;

    return (
      <Portal name={portalName}>
        <div
          className={classnames(
            'end-0 top-0 isolate z-8 overflow-x-hidden py-20',
            {
              absolute: !isFixed,
              fixed: isFixed,
              'pointer-events-none': !visible,
              'px-20': visible,
            },
            className,
          )}
        >
          <div
            {...rest}
            aria-hidden={!visible}
            className={classnames('transform transition-all duration-250 ease-in', {
              'pointer-events-auto opacity-0 ltr:translate-x-full rtl:-translate-x-full': !visible,
              'translate-x-0': visible,
            })}
          >
            <div
              className={classnames(thCard, `text-${color}`, {
                'group relative overflow-hidden': !!duration,
                relative: theme === 'advanced',
                'shadow-md': visible,
              })}
              data-testid="Toast"
              ref={ref}
              // On the element the ref exposes, so that a layer keeping it usable keeps it heard.
              role={role}
            >
              <div className={thContent}>
                <Icon className="shrink-0" name={icon} width="1.875rem" />
                {label && <Text className="flex-1" text={t(label)} />}
                {(title || text) && (
                  <div className="flex flex-1 flex-col text-14 text-black">
                    {title && <p className="font-bold">{title}</p>}
                    {text && <Text className="whitespace-pre-line" text={text} />}
                  </div>
                )}
                {children}
              </div>
              <button
                aria-disabled={!visible}
                aria-label={t('common.close')}
                className={thClose}
                onClick={onClose}
                tabIndex={visible ? 0 : -1}
                type="button"
              >
                <Icon name={ICONS.CROSS} width={thCloseIconWidth} />
              </button>
              {visible && !!duration && (
                <div
                  aria-hidden
                  className="group-focus-within:animation-paused group-hover:animation-paused absolute inset-x-0 bottom-0 h-4 origin-left animate-countdown bg-current rtl:origin-right"
                  data-testid="Toast-countdown"
                  onAnimationEnd={onClose}
                  // The duration is a prop: a CSS variable carries it to the animation.
                  style={{ '--tw-animation-duration': `${duration}ms` } as CSSProperties}
                />
              )}
            </div>
          </div>
        </div>
      </Portal>
    );
  },
);
